/**
 * DTC Buy Box - Optimized JavaScript Module
 * Handles product variant selection and add to cart functionality
 */

// Shopify Money Formatting Utilities
const ShopifyUtils = {
  /**
   * Formats money amount according to shop's format settings
   * @param {number|string} cents - Amount in cents (e.g., 1234 => 12.34)
   * @param {string} format - Optional Shopify money format
   * @returns {string} Formatted money string
   */
  formatMoney(cents, format) {
    // Input validation and normalization
    if (typeof cents === 'string') {
      cents = cents.includes('.') 
        ? Math.round(parseFloat(cents) * 100)
        : parseInt(cents, 10);
    }

    if (isNaN(cents)) cents = 0;

    const moneyFormat = format || window.Shopify?.money_format || '{{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    
    const formatWithDelimiters = (number, precision, thousands, decimal) => {
      precision = precision == null ? 2 : Math.max(0, precision);
      thousands = thousands || ',';
      decimal = decimal || '.';

      const negative = number < 0 ? '-' : '';
      const base = String(Math.abs(number).toFixed(precision));
      const parts = base.split('.');
      const integer = parts[0];
      const decimalPart = parts[1] || '';

      // Add thousands separators
      const withThousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

      return negative + (precision ? withThousands + decimal + decimalPart : withThousands);
    };

    const formatType = (moneyFormat.match(placeholderRegex) || [])[1];
    let value = 0;

    // Convert cents to currency unit
    switch (formatType) {
      case 'amount':
        value = formatWithDelimiters(cents / 100, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents / 100, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents / 100, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents / 100, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents / 100, 2, ',', '.');
    }

    return moneyFormat.replace(placeholderRegex, value);
  }
};

// Main DTC Buy Box Class
class DTCBuyBox {
  constructor(config = {}) {
    this.config = {
      variantButtonsSelector: '.options-wrapper .option',
      addToCartButtonSelector: '.atc-button',
      priceDisplaySelector: '.atc-button span',
      activeClass: 'option-active',
      cartEndpoint: '/cart/add.js',
      checkoutRedirect: '/checkout',
      ...config
    };

    this.elements = {};
    this.state = {
      baseUnitPrice: null,
      currentVariant: null
    };

    this.init();
  }

  /**
   * Initialize the Buy Box functionality
   */
  init() {
    this.cacheElements();
    this.validateElements();
    this.bindEvents();
    this.setInitialState();
  }

  /**
   * Cache DOM elements for better performance
   */
  cacheElements() {
    this.elements = {
      variantButtons: Array.from(document.querySelectorAll(this.config.variantButtonsSelector)),
      addToCartButton: document.querySelector(this.config.addToCartButtonSelector),
      priceDisplay: document.querySelector(this.config.priceDisplaySelector)
    };
  }

  /**
   * Validate that required elements exist
   */
  validateElements() {
    const { variantButtons, addToCartButton } = this.elements;

    if (!variantButtons.length) {
      console.warn('DTC Buy Box: No variant buttons found');
      return false;
    }

    if (!addToCartButton) {
      console.warn('DTC Buy Box: Add to cart button not found');
      return false;
    }

    return true;
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    if (!this.validateElements()) return;

    // Use event delegation for better performance
    const optionsWrapper = document.querySelector('.options-wrapper');
    if (optionsWrapper) {
      optionsWrapper.addEventListener('click', this.handleVariantSelection.bind(this));
    }

    // Add to cart functionality
    this.elements.addToCartButton.addEventListener('click', this.handleAddToCart.bind(this));
  }

  /**
   * Set initial state from first variant
   */
  setInitialState() {
    const firstVariant = this.elements.variantButtons[0];
    if (firstVariant) {
      this.state.baseUnitPrice = firstVariant.dataset.unitPrice;
      this.state.currentVariant = this.extractVariantData(firstVariant);
    }
  }

  /**
   * Handle variant button selection
   * @param {Event} event - Click event
   */
  handleVariantSelection(event) {
    const button = event.target.closest(this.config.variantButtonsSelector);
    if (!button) return;

    // Remove active class from all buttons
    this.elements.variantButtons.forEach(btn => 
      btn.classList.remove(this.config.activeClass)
    );

    // Add active class to selected button
    button.classList.add(this.config.activeClass);

    // Extract variant data
    const variantData = this.extractVariantData(button);
    this.state.currentVariant = variantData;

    // Update price display and cart button
    this.updatePriceDisplay(variantData, button !== this.elements.variantButtons[0]);
    this.updateCartButton(variantData);
  }

  /**
   * Extract variant data from button element
   * @param {HTMLElement} button - Variant button element
   * @returns {Object} Variant data
   */
  extractVariantData(button) {
    const dataset = button.dataset;
    return {
      id: dataset.id,
      availability: dataset.availability,
      testItems: dataset.testItems,
      unitPrice: dataset.unitPrice,
      price: dataset.price
    };
  }

  /**
   * Update price display with formatted prices
   * @param {Object} variantData - Current variant data
   * @param {boolean} showOldPrice - Whether to show strikethrough price
   */
  updatePriceDisplay(variantData, showOldPrice) {
    if (!this.elements.priceDisplay) return;

    const { testItems, price } = variantData;
    const { baseUnitPrice } = this.state;

    let priceHTML = '';

    // Add old price if not the first variant
    if (showOldPrice && baseUnitPrice && testItems) {
      const oldPriceAmount = (baseUnitPrice * testItems) * 100;
      const formattedOldPrice = ShopifyUtils.formatMoney(
        oldPriceAmount, 
        window.Shopify?.money_format
      );
      priceHTML += `<s>${formattedOldPrice}</s> `;
    }

    // Add current price
    const formattedCurrentPrice = ShopifyUtils.formatMoney(
      price, 
      window.Shopify?.money_format
    );
    priceHTML += formattedCurrentPrice;

    this.elements.priceDisplay.innerHTML = priceHTML;
  }

  /**
   * Update add to cart button with current variant ID
   * @param {Object} variantData - Current variant data
   */
  updateCartButton(variantData) {
    if (!this.elements.addToCartButton) return;
    this.elements.addToCartButton.dataset.currentId = variantData.id;
  }

  /**
   * Handle add to cart action
   * @param {Event} event - Click event
   */
  async handleAddToCart(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const variantId = button.dataset.currentId;

    if (!variantId) {
      console.error('DTC Buy Box: No variant selected');
      return;
    }

    // Disable button during request
    this.setButtonLoading(button, true);

    try {
      await this.addToCart(variantId);
      // Redirect to checkout on success
      window.location.href = this.config.checkoutRedirect;
    } catch (error) {
      console.error('DTC Buy Box: Error adding to cart:', error);
      this.showError('Failed to add item to cart. Please try again.');
    } finally {
      this.setButtonLoading(button, false);
    }
  }

  /**
   * Clear all items from cart via Shopify Cart API
   * @returns {Promise} Cart API response
   */
  async clearCart() {
    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Add items to cart via Shopify Cart API
   * First clears the cart, then adds multiple items including the selected variant and additional variant
   * @param {string} variantId - Variant ID to add
   * @param {number} quantity - Quantity to add (default: 1)
   * @returns {Promise} Cart API response
   */
  async addToCart(variantId, quantity = 1) {
    // Clear cart before adding new items
    await this.clearCart();

    const payload = {
      items: [
        {
          id: variantId,
          quantity: quantity
        },
        {
          id: '44098399502380',
          quantity: 1,
          selling_plan: 1314455596
        }
      ]
    };

    const response = await fetch(this.config.cartEndpoint, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Set button loading state
   * @param {HTMLElement} button - Button element
   * @param {boolean} isLoading - Loading state
   */
  setButtonLoading(button, isLoading) {
    if (isLoading) {
      button.disabled = true;
      button.classList.add('loading');
    } else {
      button.disabled = false;
      button.classList.remove('loading');
    }
  }

  /**
   * Show error message to user
   * @param {string} message - Error message
   */
  showError(message) {
    // Simple console error for now - can be enhanced with UI notifications
    console.error(message);
    
    // Could be enhanced with toast notifications or inline error display
    // Example: this.showToast(message, 'error');
  }

  /**
   * Destroy the instance and clean up event listeners
   */
  destroy() {
    const optionsWrapper = document.querySelector('.options-wrapper');
    if (optionsWrapper) {
      optionsWrapper.removeEventListener('click', this.handleVariantSelection.bind(this));
    }

    if (this.elements.addToCartButton) {
      this.elements.addToCartButton.removeEventListener('click', this.handleAddToCart.bind(this));
    }
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize Shopify object if not present
  window.Shopify = window.Shopify || {};
  
  // Initialize DTC Buy Box
  window.dtcBuyBox = new DTCBuyBox();
});

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { DTCBuyBox, ShopifyUtils };
}
